import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Package, LogOut, Bell, Navigation } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge, ActionButton, EmptyState } from "../../components/common";
import { DriverPickup } from "../../components/fractank/DriverPickup";
import { RentalTypeBadge } from "../../components/fractank/RentalTypeBadge";
import { RentalPhotos } from "../../components/fractank/RentalPhotos";
import { TankTripPanel } from "../../components/fractank/TankTripPanel";
import { DriverWorkOrderRequest } from "../../components/fleet/DriverWorkOrderRequest";
import MiniMap from "../../components/MiniMap";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export default function MobileDriver() {
  const { user, logout, hasModule } = useAuth();
  const rentalsEnabled = hasModule("fractank");
  const navigate = useNavigate();
  const [dispatches, setDispatches] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [tripBusy, setTripBusy] = useState(null);
  const [haulEquip, setHaulEquip] = useState({ trucks: [], trailers: [] });
  const [alerts, setAlerts] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const enableAlerts = async () => {
    try {
      const perm = await Notification.requestPermission();
      setAlerts(perm);
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.register("/sw.js");
      const keyRes = await api.get("/push/vapid-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(keyRes.data.public_key) });
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      toast.success("Job alerts enabled");
    } catch (e) {
      toast.error("Notifications not available on this device");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const load = useCallback(() => {
    if (!rentalsEnabled) return Promise.resolve();
    return api.get("/fractank/dispatches")
      .then((r) => setDispatches(r.data.filter((d) => (d.drivers?.length ? d.drivers : [d.driver_name]).filter(Boolean).includes(user?.name))))
      .catch((e) => toast.error(errMsg(e)));
  }, [user?.name, rentalsEnabled]);

  useEffect(() => {
    if (user?.name) load();
    const iv = setInterval(() => {
      if (user?.name) load();
    }, 10000);
    return () => clearInterval(iv);
  }, [load, user?.name]);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", "/manifest-driver.json");
    return () => {
      if (link) link.setAttribute("href", "/manifest.json");
    };
  }, []);

  const accept = async (d) => {
    setBusyId(d.id);
    try {
      await api.patch(`/fractank/dispatches/${d.id}`, { driver_accepted: true });
      toast.success(`${d.dispatch_number} accepted`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const confirm = async (d) => {
    setBusyId(d.id);
    try {
      await api.patch(`/fractank/dispatches/${d.id}`, { status: d.return_to_yard ? "dropped_off" : "on_rent" });
      toast.success(`${d.dispatch_number} confirmed — ${d.return_to_yard ? "DROP OFF WASH YARD" : "ON RENT"}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    if (rentalsEnabled) api.get("/fractank/haul-equipment").then((r) => setHaulEquip(r.data)).catch(() => {});
  }, [rentalsEnabled]);

  const setEquip = async (d, patch) => {
    try {
      await api.patch(`/fractank/dispatches/${d.id}/driver-equipment`, patch);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const tankTrip = async (d, assetId, event) => {
    setTripBusy(`${d.id}:${assetId}`);
    try {
      await api.patch(`/fractank/dispatches/${d.id}/tank-trip`, { asset_id: assetId, event });
      toast.success(event === "start" ? "Pick-up time started" : "Drop-off time recorded");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTripBusy(null);
    }
  };

  const completed = (d) => ["on_rent", "returned", "dropped_off", "ready_to_rent", "closed"].includes(d.status) && !d.pickup_confirmation_needed;
  const visible = dispatches.filter((d) => showCompleted ? completed(d) : !completed(d));

  return (
    <div className="min-h-screen bg-background tactical-grid" data-testid="mobile-driver-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-800 px-4 py-3 flex items-center gap-3">
        <img src="/logo.png" alt="Well Fleet Logic" className="w-9 h-9 rounded bg-white p-0.5 object-contain border border-slate-800" />
        <div>
          <p className="font-heading text-lg font-bold tracking-wide text-slate-50 leading-none">MY DISPATCHES</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-500/80">Rental Logic</p>
        </div>
        <button data-testid="mobile-driver-logout" onClick={handleLogout} className="ml-auto flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-red-500 transition-colors">
          <LogOut size={15} /> Logout
        </button>
      </header>

      <main className="max-w-md mx-auto p-4 pb-12 space-y-4">
        {alerts === "default" && (
          <button data-testid="mobile-driver-enable-alerts" onClick={enableAlerts} className="w-full flex items-center justify-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2.5 text-xs font-mono uppercase tracking-wider text-sky-600">
            <Bell size={14} /> Enable job alerts
          </button>
        )}
        {user?.role === "driver" && hasModule("fleet") && <DriverWorkOrderRequest />}
        <p className="text-xs text-slate-500 font-mono uppercase tracking-widest pt-2" data-testid="mobile-driver-name">Driver: {user?.name}</p>

        <ActionButton testId="mobile-driver-completed-toggle" variant="secondary" onClick={() => setShowCompleted((value) => !value)} className="w-full justify-center">{showCompleted ? "Show Active Assignments" : "Completed Assignments / Photos"}</ActionButton>
        {visible.length === 0 ? (
          <EmptyState message={showCompleted ? "No completed assignments" : "No dispatches assigned to you"} testId="mobile-driver-empty" />
        ) : (
          visible.map((d) => (
            <div key={d.id} className={`rounded-lg p-4 space-y-2 border [overflow-wrap:anywhere] ${d.rental_type === "mixing_plant" ? "bg-pink-50 border-pink-300" : d.job_type === "return_pickup" ? "bg-emerald-50/60 border-emerald-300" : "bg-card border-slate-700/60"}`} data-testid={`mobile-driver-card-${d.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-amber-500">{d.dispatch_number}</span>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  <RentalTypeBadge job={d} prefix="driver" />
                  {d.job_type === "return_pickup" && (
                    <span data-testid={`mobile-driver-pickup-chip-${d.id}`} className="inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider bg-emerald-500/10 text-emerald-600 border-emerald-500/30">Return Pickup</span>
                  )}
                  <StatusBadge status={d.status} />
                </div>
              </div>
              <p className="text-base font-medium text-slate-50">{d.customer_name || "—"}</p>
              <p className="text-sm text-slate-400">{d.delivery_location || d.geo_address || "—"}</p>
              <p data-testid={`mobile-driver-destination-${d.id}`} className="rounded-md border border-slate-300 bg-white p-2 text-sm font-medium">{d.return_to_yard ? "Destination: Return to Yard" : d.job_type === "return_pickup" ? "Pickup location above · new destination assigned as a separate rental" : `Destination: ${d.delivery_location || d.geo_address || (d.lat != null && d.lng != null ? `${d.lat}, ${d.lng}` : "Ask dispatcher for location")}`}</p>
              {(() => {
                const navDest = d.lat != null && d.lng != null ? `${d.lat},${d.lng}` : (d.delivery_location || d.geo_address || "");
                if (!navDest || (d.return_to_yard && !(d.lat != null && d.lng != null))) return null;
                return (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(navDest)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`mobile-driver-navigate-${d.id}`}
                    className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white font-heading uppercase tracking-wide text-sm rounded-md py-2.5 transition-colors"
                  >
                    <Navigation size={16} /> Navigate
                  </a>
                );
              })()}
              {(d.drivers?.length > 0 || d.driver_name) && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`mobile-driver-bubbles-${d.id}`}>
                  {(d.drivers?.length ? d.drivers : [d.driver_name]).filter(Boolean).map((n) => {
                    const acc = (d.drivers_accepted || []).includes(n) || (d.driver_accepted && !(d.drivers_accepted || []).length);
                    return (
                      <span key={n} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-mono ${acc ? "bg-sky-500/15 border-sky-400/60 text-sky-600" : "bg-slate-900 border-slate-700 text-slate-400"}`}>
                        {n}{acc ? " ✓" : ""}
                      </span>
                    );
                  })}
                </div>
              )}
              {d.lat != null && d.lng != null && (
                <div className="pt-1 space-y-1.5">
                  {d.job_type === "return_pickup" && <p data-testid={`mobile-driver-map-label-${d.id}`} className="text-xs text-slate-500">Pickup location{d.return_to_yard ? " · Return to Yard after pickup" : ""}</p>}
                  <MiniMap lat={d.lat} lng={d.lng} testId={`mobile-driver-map-${d.id}`} interactive />
                </div>
              )}
              {(d.asset_tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {d.asset_tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-900 border border-slate-700 text-xs font-mono text-slate-300">
                      <Package size={11} /> {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="text-xs text-slate-500 font-mono space-y-0.5 pt-1">
                {d.job_type === "return_pickup" && d.pickup_date && <p>Pick-up {fmtDate(d.pickup_date)}</p>}
                {d.job_type === "return_pickup" && d.return_destination && <p>Destination: {d.return_destination}</p>}
                {d.deliver_by_date && <p>Deliver by {fmtDate(d.deliver_by_date)}</p>}
                {d.billing_start_date && <p>Billing starts {fmtDate(d.billing_start_date)}</p>}
                {d.notes && <p className="text-slate-400">{d.notes}</p>}
              </div>
              {!completed(d) && ((d.drivers_accepted || []).includes(user?.name) || (d.driver_accepted && !(d.drivers_accepted || []).length) ? (
                <p className="text-center text-xs font-mono uppercase tracking-widest text-emerald-600" data-testid={`mobile-driver-accepted-${d.id}`}>Accepted ✓</p>
              ) : (
                <ActionButton
                  testId={`mobile-driver-accept-${d.id}`}
                  variant="primary"
                  onClick={() => accept(d)}
                  disabled={busyId === d.id}
                  className="w-full justify-center !py-3 mt-2"
                >
                  {busyId === d.id ? "Accepting…" : d.job_type === "return_pickup" ? "Accept Pickup Job" : "Accept Job"}
                </ActionButton>
              ))}
              <RentalPhotos job={d} canUpload prefix="driver" />
              <TankTripPanel d={d} user={user} busy={tripBusy} onTrip={tankTrip} trucks={haulEquip.trucks} trailers={haulEquip.trailers} defaults={haulEquip.defaults} onEquip={setEquip} />
              {!completed(d) && d.job_type === "return_pickup" && <DriverPickup job={d} user={user} onDone={load} busyId={busyId} setBusyId={setBusyId} />}
              {d.status === "delivered" && d.job_type !== "return_pickup" && (
                <ActionButton
                  testId={`mobile-driver-confirm-${d.id}`}
                  variant={((d.drivers_accepted || []).includes(user?.name) || (d.driver_accepted && !(d.drivers_accepted || []).length)) ? "primary" : "secondary"}
                  onClick={() => confirm(d)}
                  disabled={busyId === d.id || !((d.drivers_accepted || []).includes(user?.name) || d.driver_accepted)}
                  className="w-full justify-center !py-3.5 !text-base mt-2"
                >
                  <CheckCircle2 size={18} /> {busyId === d.id ? "Confirming…" : d.return_to_yard ? "Finished — Returned to Yard" : "Confirm Delivery — On Rent"}
                </ActionButton>
              )}
              {d.status === "on_rent" && (
                <p className="text-center text-xs font-mono uppercase tracking-widest text-emerald-600 pt-2" data-testid={`mobile-driver-onrent-${d.id}`}>On Rent — confirmed</p>
              )}
            </div>
          ))
        )}
      </main>
    </div>
  );
}
